// Docs: https://www.instantdb.com/docs/modeling-data
// Auto-pushed to InstantDB by CI on changes to this file.

import { i } from "@instantdb/core";
import { z } from "zod/v4";
import type { JsonSchema } from "json-schema-to-zod";

export const remoteToolSchema = z.object({
  name: z.string().describe("Tool name the agent uses to invoke this tool."),
  description: z.string().describe(
    "Human-readable description of what the tool does.",
  ),
  url: z.string().url().refine(
    (u) => /^https?:\/\//.test(u),
    "URL must start with http:// or https:// (do not include the HTTP method)",
  ).describe("HTTPS endpoint the bot POSTs to when calling."),
  parameters: z.any() as z.ZodType<JsonSchema>,
});

export type RemoteTool = z.infer<typeof remoteToolSchema>;

export const remoteSkillSchema = z.object({
  name: z.string().describe("Skill name."),
  description: z.string().describe(
    "Human-readable description of the skill.",
  ),
  instructions: z.string().describe(
    "Instructions the agent receives when using this skill.",
  ),
  tools: z.array(remoteToolSchema).describe(
    "HTTP tool endpoints grouped under this skill.",
  ),
});

export type RemoteSkill = z.infer<typeof remoteSkillSchema>;

const _schema = i.schema({
  // We inferred 4 attributes!
  // Take a look at this schema, and if everything looks good,
  // run `push schema` again to enforce the types.
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.string(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed().optional(),
      imageURL: i.string().optional(),
      type: i.string().optional(),
      agentRunCount: i.number().indexed().optional(),
      llmRunCount: i.number().indexed().optional(),
      aliceAndBotCredentials: i.json().optional(),
      billingPeriodStart: i.number().indexed().optional(),
      candidatesTokenCount: i.number().indexed().optional(),
      imageGenerationCount: i.number().indexed().optional(),
      decisionModelCount: i.number().indexed().optional(),
      incomingMessageCount: i.number().indexed().optional(),
      promptTokenCount: i.number().indexed().optional(),
      thoughtsTokenCount: i.number().indexed().optional(),
      cachedContentTokenCount: i.number().indexed().optional(),
      failedPaymentTimestamp: i.number().indexed().optional(),
      lastVisit: i.number().indexed().optional(),
      pendingAuthToken: i.string().optional(),
      polarCustomerId: i.string().unique().indexed().optional(),
      polarSubscriptionId: i.string().unique().indexed().optional(),
      stripeCustomerId: i.string().unique().indexed().optional(),
      stripeSubscriptionId: i.string().unique().indexed().optional(),
      phone: i.string().unique().indexed().optional(),
      signupTimestamp: i.number().indexed().optional(),
      trialStartedAt: i.number().indexed().optional(),
      telegramId: i.string().unique().indexed().optional(),
      tier: i.string().indexed().optional(),
      unsubscribeFromNewsletter: i.boolean().indexed().optional(),
      attribution: i.string().indexed().optional(),
      signupIp: i.string().indexed().optional(),
      lastIp: i.string().indexed().optional(),
    }),
    // NOTE: several raw third-party provider credentials live directly on this
    // entity (e.g. facebookMessengerPageAccessToken, facebookMessengerAppSecret,
    // instagramPageAccessToken, instagramAppSecret, slackBotToken,
    // slackSigningSecret, teamsAppPassword, whatsappForBusinessToken,
    // whatsappForBusinessAppSecret, calendlyToken, forwardEmailApiKey). Any bot
    // admin can read these via `bots.view: isAdminOfBot`. Do NOT add new raw
    // credential fields here — store them encrypted in `secrets`/`envVariables`
    // (which are decrypted server-side) instead.
    bots: i.entity({
      agentRunCount: i.number().indexed().optional(),
      llmRunCount: i.number().indexed().optional(),
      billingPeriodStart: i.number().indexed().optional(),
      candidatesTokenCount: i.number().indexed().optional(),
      imageGenerationCount: i.number().indexed().optional(),
      decisionModelCount: i.number().indexed().optional(),
      aliceAndBotCredentials: i.json(),
      incomingMessageCount: i.number().indexed().optional(),
      promptTokenCount: i.number().indexed().optional(),
      thoughtsTokenCount: i.number().indexed().optional(),
      cachedContentTokenCount: i.number().indexed().optional(),
      calendlyToken: i.string().indexed().optional(),
      enableCodeExecution: i.boolean().indexed().optional(),
      enableImageGeneration: i.boolean().indexed().optional(),
      enableWebSearch: i.boolean().indexed().optional(),
      enableBrowserUse: i.boolean().indexed().optional(),
      enableHandoff: i.boolean().indexed().optional(),
      showToolUseSpinner: i.boolean().indexed().optional(),
      facebookMessengerPageAccessToken: i
        .string()
        .unique()
        .indexed()
        .optional(),
      facebookMessengerVerifyToken: i.string().indexed().optional(),
      facebookMessengerAppSecret: i.string().indexed().optional(),
      instagramPageAccessToken: i
        .string()
        .unique()
        .indexed()
        .optional(),
      instagramVerifyToken: i.string().optional(),
      instagramAppSecret: i.string().optional(),
      forwardEmailApiKey: i.string().indexed().optional(),
      groupChatBehavior: i.string().indexed().optional(),
      slackBotToken: i.string().unique().indexed().optional(),
      slackSigningSecret: i.string().indexed().optional(),
      slackAppId: i.string().unique().indexed().optional(),
      slackBotUserId: i.string().indexed().optional(),
      forwardEmailFromAddress: i.string().indexed().optional(),
      gmailTokenEnvName: i.string().indexed().optional(),
      gmailHistoryId: i.string().optional(),
      gmailAddress: i.string().indexed().optional(),
      injectAds: i.boolean().indexed(),
      isolateUsers: i.boolean().indexed(),
      name: i.string().indexed(),
      preferredProvider: i.string().indexed().optional(),
      prompt: i.string(),
      promptHistory: i.json().optional(),
      webhookSecret: i.string().unique().indexed().optional(),
      remoteToolsSecret: i.string().unique().indexed().optional(),
      remoteSkills: i.json().optional(),
      webhookApps: i.json().optional(),
      telegramToken: i.string().unique().indexed().optional(),
      telegramUsername: i.string().unique().indexed().optional(),
      teamsAppId: i.string().unique().indexed().optional(),
      teamsAppPassword: i.string().indexed().optional(),
      timezoneIANA: i.string().indexed(),
      blockedUsers: i.json().optional(),
      tools: i.json().optional(),
      customRecorderWebhookUrl: i.string().indexed().optional(),
      deprecationNotice: i.string().optional(),
      errorMessage: i.string().optional(),
      viewChatGroupId: i.string().indexed(),
      whatsappForBusinessToken: i.string().unique().indexed().optional(),
      whatsappForBusinessVerifyCode: i.string().indexed().optional(),
      whatsappForBusinessWabaId: i.string().indexed().optional(),
      whatsappForBusinessAppSecret: i.string().indexed().optional(),
      twilioPhoneNumber: i.string().unique().indexed().optional(),
      voiceName: i.string().optional(),
    }),
    files: i.entity({
      hash: i.string().indexed(),
      name: i.string().indexed(),
      type: i.string().indexed(),
    }),
    folders: i.entity({
      name: i.string().indexed(),
      pineconeName: i.string().unique().indexed(),
    }),
    projects: i.entity({
      createdAt: i.number().indexed(),
      createdInConversation: i.string().indexed().optional(),
      name: i.string().indexed(),
      stopped: i.boolean().indexed(),
      updatedAt: i.number().indexed(),
    }),
    ragDocuments: i.entity({
      refDocId: i.string().indexed(),
    }),
    tasks: i.entity({
      createdAt: i.number().indexed(),
      createdInConversation: i.string().indexed().optional(),
      description: i.string(),
      nextRunAt: i.number().indexed().optional(),
      preferredNetwork: i.string().indexed(),
      projectId: i.string().indexed().optional(),
      recurrenceEnd: i.string().indexed().optional(),
      recurrenceRule: i.string().indexed().optional(),
      resolved: i.boolean().indexed(),
      runAt: i.number().indexed(),
      status: i.string().indexed(),
      subject: i.string().optional(),
      targetConversationId: i.string().indexed().optional(),
      aliceChatUserId: i.string().indexed().optional(),
      whatsappTemplateName: i.string().indexed().optional(),
      whatsappTemplateLanguage: i.string().indexed().optional(),
      whatsappTemplateVariables: i.json().optional(),
      whatsappTemplateHeaderImageUrl: i.string().optional(),
      contactName: i.string().optional(),
      phoneCallVoice: i.string().optional(),
      phoneCallLanguage: i.string().optional(),
      phoneCallGreeting: i.string().optional(),
    }),
    machines: i.entity({
      conversationId: i.string().indexed().optional(),
      createdAt: i.number().indexed(),
      encryptedGeminiKey: i.string().optional(),
      expiresAt: i.number().indexed().optional(),
      hetznerServerId: i.number().indexed(),
      ip: i.string().indexed().optional(),
      ipv6Prefix: i.string().indexed().optional(),
      lastCommandAt: i.number().indexed().optional(),
      readyCallbackPayload: i.json().optional(),
      readyToken: i.string().optional(),
      cachedDockerImageRef: i.string().optional(),
      serverType: i.string().indexed(),
      signingPrivateKey: i.string().optional(),
      signingPublicKeyPem: i.string().optional(),
      status: i.string().indexed(),
    }),
    envVariables: i.entity({
      name: i.string().indexed(),
      encryptedValue: i.string(),
      hosts: i.json(),
      isSecret: i.boolean().indexed(),
      plaintextValue: i.string().optional(),
    }),
    secrets: i.entity({
      name: i.string().indexed(),
      encryptedValue: i.string(),
      hosts: i.json(),
      conversationId: i.string().indexed().optional(),
    }),
    variables: i.entity({
      name: i.string().indexed(),
      value: i.string(),
    }),
    apiTokens: i.entity({
      name: i.string().indexed(),
      tokenHash: i.string().unique().indexed(),
      createdAt: i.number().indexed(),
    }),
    botApiTokens: i.entity({
      name: i.string().indexed(),
      tokenHash: i.string().unique().indexed(),
      createdAt: i.number().indexed(),
    }),
    skills: i.entity({
      sourceUrl: i.string().indexed(),
      installedAt: i.number().indexed(),
    }),
    whatsappBusinessAccounts: i.entity({
      wabaId: i.string().unique().indexed(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    whatsappBusinessNumbers: i.entity({
      phoneNumberId: i.string().unique().indexed(),
      displayNumber: i.string().indexed(),
      codeVerificationStatus: i.string().indexed().optional(),
      callingStatus: i.string().indexed().optional(),
      isDisabled: i.boolean().indexed().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    whatsappBusinessTemplates: i.entity({
      name: i.string().indexed(),
      language: i.string().indexed(),
      category: i.string().indexed(),
      body: i.string(),
      purpose: i.string().indexed(),
      status: i.string().indexed().optional(),
      metaTemplateId: i.string().indexed().optional(),
      createdAt: i.number().indexed(),
      updatedAt: i.number().indexed(),
    }),
    supergreenWaAccounts: i.entity({
      phoneNumber: i.string().unique().indexed(),
      secretToken: i.string().indexed(),
      createdAt: i.number().indexed(),
      isConnected: i.boolean().indexed().optional(),
      lastSetupConversationId: i.string().optional().indexed(),
      lastSetupBotId: i.string().optional().indexed(),
    }),
    demoNumbers: i.entity({
      number: i.string().unique().indexed(),
      blocked: i.boolean().indexed(),
      kind: i.string().indexed(),
      createdAt: i.number().indexed(),
      reservedForPayingUsers: i.boolean().indexed().optional(),
    }),
    demoNumberTokens: i.entity({
      number: i.string().unique().indexed(),
      secretToken: i.string().optional(),
      businessToken: i.string().optional(),
      businessWabaId: i.string().optional(),
      businessAppSecret: i.string().optional(),
      businessVerifyToken: i.string().optional(),
    }),
    referralVisits: i.entity({
      referrerId: i.string().indexed(),
      visitedAt: i.number().indexed(),
    }),
    mentatInviteCodes: i.entity({
      code: i.string().unique().indexed(),
      createdAt: i.number().indexed(),
      usedAt: i.number().indexed().optional(),
      usedByEmail: i.string().indexed().optional(),
      generatedByEmail: i.string().indexed().optional(),
    }),
  },
  links: {
    $usersLinkedPrimaryUser: {
      forward: {
        on: "$users",
        has: "one",
        label: "linkedPrimaryUser",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "linkedGuestUsers",
      },
    },
    botsFolders: {
      forward: {
        on: "bots",
        has: "many",
        label: "folders",
      },
      reverse: {
        on: "folders",
        has: "many",
        label: "bots",
      },
    },
    botsProjects: {
      forward: {
        on: "bots",
        has: "many",
        label: "projects",
      },
      reverse: {
        on: "projects",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsTasks: {
      forward: {
        on: "bots",
        has: "many",
        label: "tasks",
      },
      reverse: {
        on: "tasks",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    filesRagDocuments: {
      forward: {
        on: "files",
        has: "many",
        label: "ragDocuments",
      },
      reverse: {
        on: "ragDocuments",
        has: "one",
        label: "file",
        onDelete: "cascade",
      },
    },
    foldersFiles: {
      forward: {
        on: "folders",
        has: "many",
        label: "files",
      },
      reverse: {
        on: "files",
        has: "one",
        label: "folder",
        onDelete: "cascade",
      },
    },
    usersBots: {
      forward: {
        on: "$users",
        has: "many",
        label: "bots",
      },
      reverse: {
        on: "bots",
        has: "many",
        label: "admins",
      },
    },
    botsBillingUser: {
      forward: {
        on: "bots",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "billedBots",
      },
    },
    botsMachines: {
      forward: {
        on: "bots",
        has: "many",
        label: "machines",
      },
      reverse: {
        on: "machines",
        has: "many",
        label: "bots",
      },
    },
    botsEnvVariables: {
      forward: {
        on: "bots",
        has: "many",
        label: "envVariables",
      },
      reverse: {
        on: "envVariables",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsSupergreenWaAccounts: {
      forward: {
        on: "bots",
        has: "many",
        label: "supergreenWaAccounts",
      },
      reverse: {
        on: "supergreenWaAccounts",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsSecrets: {
      forward: {
        on: "bots",
        has: "many",
        label: "secrets",
      },
      reverse: {
        on: "secrets",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsVariables: {
      forward: {
        on: "bots",
        has: "many",
        label: "variables",
      },
      reverse: {
        on: "variables",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsSkills: {
      forward: {
        on: "bots",
        has: "many",
        label: "skills",
      },
      reverse: {
        on: "skills",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    botsWhatsappBusinessAccounts: {
      forward: {
        on: "bots",
        has: "many",
        label: "whatsappBusinessAccounts",
      },
      reverse: {
        on: "whatsappBusinessAccounts",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
    whatsappBusinessAccountsNumbers: {
      forward: {
        on: "whatsappBusinessAccounts",
        has: "many",
        label: "numbers",
      },
      reverse: {
        on: "whatsappBusinessNumbers",
        has: "one",
        label: "businessAccount",
        onDelete: "cascade",
      },
    },
    whatsappBusinessNumbersTemplates: {
      forward: {
        on: "whatsappBusinessNumbers",
        has: "many",
        label: "templates",
      },
      reverse: {
        on: "whatsappBusinessTemplates",
        has: "one",
        label: "number",
        onDelete: "cascade",
      },
    },
    usersApiTokens: {
      forward: {
        on: "$users",
        has: "many",
        label: "apiTokens",
      },
      reverse: {
        on: "apiTokens",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
    },
    botsBotApiTokens: {
      forward: {
        on: "bots",
        has: "many",
        label: "botApiTokens",
      },
      reverse: {
        on: "botApiTokens",
        has: "one",
        label: "bot",
        onDelete: "cascade",
      },
    },
  },
  rooms: {},
});

// This helps Typescript display nicer intellisense
type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
